import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

import { api } from './client';

export type ManagedKeyProvider = 'anthropic' | 'openai' | 'openrouter';

export interface ManagedKey {
  id: string;
  provider: ManagedKeyProvider;
  /** Last 4 plaintext chars only — the raw key is never returned. */
  keyHint: string | null;
  label: string | null;
  isActive: boolean;
  priority: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastUsedAt: string | null;
}

const MANAGED_KEYS_KEY: QueryKey = ['admin', 'managed-keys'];

export function useManagedKeys() {
  return useQuery({
    queryKey: MANAGED_KEYS_KEY,
    queryFn: () => api<ManagedKey[]>('/api/admin/managed-keys'),
  });
}

export interface SetManagedKeyInput {
  provider: ManagedKeyProvider;
  key: string;
  label?: string;
  reason: string;
}

export function useSetManagedKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetManagedKeyInput) =>
      api<ManagedKey>('/api/admin/managed-keys', { method: 'POST', body: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MANAGED_KEYS_KEY }),
  });
}

export function useDeleteManagedKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<ManagedKey>(`/api/admin/managed-keys/${id}`, { method: 'DELETE', body: { reason } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MANAGED_KEYS_KEY }),
  });
}
